import Cookies from 'js-cookie';

import type { IProfile } from '@/assets/types/api/Profile';

const getDefaultProfile = (): IProfile => ({
  firstName: null,
  lastName: null,
  companyName: null,
  email: null,
  country: null,
  mobileCountryCode: null,
  mobileNumber: null,
  bu: null,
  point: 0,
  currency: 'USD',
  memberType: 0,
  isSales: false,
  isAdmin: false,
  isLogin: false,
  isValidOrg: true,
  maintenanceTimePeriod: null,
  hasMultipleAccounts: false,
  role: null,
  erpId: null,
  accountId: null,
  regionCode: null,
  accountName: null,
});

export const useProfileStore = defineStore('profileStore', () => {
  const nuxtApp = useNuxtApp();
  const runtimeConfig = useRuntimeConfig();

  /* State */

  const profile = ref<IProfile>(getDefaultProfile());
  const isUserVerifying = ref<boolean>(false);

  /* Getters */

  const isLogin = computed((): boolean => profile.value.isLogin);

  const userName = computed((): string => {
    const { firstName, lastName } = profile.value;
    return `${firstName || ''} ${lastName || ''}`;
  });

  const userNameAbbrev = computed((): string => {
    return provideAbbrev(userName.value);
  });

  const userAccountName = computed((): string => {
    const { accountName } = profile.value;
    if (accountName) {
      return accountName;
    }
    return '';
  });

  const userAccountNameAbbrev = computed((): string => {
    return provideAbbrev(userAccountName.value);
  });

  const userRole = computed((): string => {
    const { role } = profile.value;
    if (role) {
      return role.split('WISE PaaS').join('');
    }
    return '';
  });

  const userMemberType = computed((): EnumMemberType => {
    const { memberType } = profile.value;
    return provideMemberTypeName(
      memberType,
      EnumMemberType.REGULAR
    ) as EnumMemberType;
  });

  const userBalance = computed((): string => {
    const { provideBalance } = useFinance();
    const { currency, point } = profile.value;
    return provideBalance({ currency, point });
  });

  /* Actions */

  const GET_PROFILE = async (): Promise<void> => {
    /**
     * 取得客戶資訊（Client Only）
     */
    const { $i18n, $customFetch, $notification } = nuxtApp;
    const { locale } = $i18n;

    // isUserVerifying.value = true;

    try {
      const data = await $customFetch<IProfile>(
        `/marketplace/api/v1/profile?websiteCode=${locale.value}`,
        { method: 'GET' }
      );

      Object.assign(profile.value, data);
    } catch (error) {
      $notification({
        message: (error as { message: string }).message,
        type: 'error',
      });
    } finally {
      isUserVerifying.value = false;
    }
  };

  const RESET_PROFILE = (): void => {
    Object.assign(profile.value, getDefaultProfile());
  };

  const HANDLE_LOGIN = (callbackUrl: string = ''): string => {
    /**
     * 登入
     */
    const { $i18n } = nuxtApp;
    const { locale } = $i18n;
    const { API_DOMAIN } = runtimeConfig.public;

    const loginUrl = (() => {
      if (callbackUrl) {
        return `${API_DOMAIN}/marketplace/api/v1/sso/login?websiteCode=${locale.value}&callbackurl=${callbackUrl}`;
      }
      return `${API_DOMAIN}/marketplace/api/v1/sso/login?websiteCode=${locale.value}`;
    })();

    Cookies.remove('access_token');

    if (import.meta.client) {
      window.location.href = loginUrl;
    }

    return loginUrl;
  };

  const HANDLE_LOGOUT = (): void => {
    /**
     * 登出
     */
    const { $i18n } = nuxtApp;
    const { locale } = $i18n;
    const { API_DOMAIN } = runtimeConfig.public;

    Cookies.remove('access_token');

    if (import.meta.client) {
      window.location.href = `${API_DOMAIN}/marketplace/api/v1/sso/logout?websiteCode=${locale.value}`;
    }
  };

  return {
    profile,
    isUserVerifying,
    isLogin,
    userName,
    userNameAbbrev,
    userAccountName,
    userAccountNameAbbrev,
    userRole,
    userMemberType,
    userBalance,
    GET_PROFILE,
    RESET_PROFILE,
    HANDLE_LOGIN,
    HANDLE_LOGOUT,
  };
});
